import { ref } from 'vue';

export type ToastColor = 'neutral' | 'success' | 'danger';

export interface ToastOptions {
  title: string;
  description?: string;
  color?: ToastColor;
  /** Milliseconds on screen; `UiToaster`'s default when omitted. */
  duration?: number;
}

export interface ToastItem extends ToastOptions {
  id: number;
  open: boolean;
}

let nextId = 0;

/**
 * One queue per app, rendered by the single `UiToaster` mounted at its root.
 * Internal: callers only `add` and `dismiss`.
 */
export const toastQueue = ref<ToastItem[]>([]);

/**
 * Show a short, non-blocking message. Text is the caller's (already
 * translated). `danger` toasts are announced assertively, the rest politely.
 */
export const useToast = () => {
  const add = (options: ToastOptions): number => {
    // A closed toast stays in the list until its exit animation has run;
    // drop those here instead of timing the animation.
    toastQueue.value = toastQueue.value.filter((toast) => toast.open);

    const id = ++nextId;

    toastQueue.value.push({ ...options, id, open: true });

    return id;
  };

  const dismiss = (id: number) => {
    const toast = toastQueue.value.find((item) => item.id === id);

    if (toast) {
      toast.open = false;
    }
  };

  return {
    add,
    dismiss,
  };
};
